import React, { useEffect } from 'react';
import styles from '../stripes.module.scss';
import { observer } from 'mobx-react-lite';
import { Collections } from '@smart-tv/components/collections';
import ProviderUniverseMenu from '@smart-tv/components/provider/provider-universe-menu';
import StripesSkeleton from '../components/stripes-skeleton';
import StripeSkeleton from '../components/stripe-skeleton';
import StripesUIElement from '../components/stripes-ui-element';
import useStripesProvider from './use-stripes-provider';
import StripesContainer from '../components/stripes-container';
import { rootStore } from '../_stores/store-root';
import { NetworkErrorDialog } from '@smart-tv/components/error-dialog/network-error-dialog';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import { Props } from '../_utils/types';

export const StripesProvider = observer(({ pageType }: Props) => {
  const {
    ref,
    focusKey,
    uiElements,
    heroUiElements,
    activeUIElement,
    loading,
    openCollections,
    handleonShowAllEnterPress,
    onHeroUIElementFocus,
    universeTitle,
    navigationContent,
    onProviderMenuUIElementFocus,
    isProviderMenuRendered,
    setIsProviderMenuRendered,
    isHeroRendered,
    retryNetworkAction,
    lastNetworkErrorSource,
    onProviderUIElementFocus
  } = useStripesProvider({ pageType });
  const { isOffline } = useNetwork();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isFilmUniverseSelected = navigationContent?.some(
    (item) => item.selected && item.label === 'Film'
  );

  const showSkeleton = loading && !heroUiElements && !uiElements?.length;

  return (
    <React.Fragment>
      {showSkeleton ? (
        <StripesSkeleton />
      ) : (
        <StripesContainer focusKey={focusKey} scrollRef={ref}>
          {heroUiElements && (
          <StripesUIElement
            universeTitle={universeTitle}
            uIElementIndex={0}
            key={heroUiElements.id}
            uiElement={heroUiElements}
            onFocus={onHeroUIElementFocus}
            handleShowAllEnterPress={() => handleonShowAllEnterPress(heroUiElements)}
            contentSource={heroUiElements.contentSource}
            providerContentProps={heroUiElements.providerContentProps}
            style={heroUiElements.style}
            focusFirstElement={true}
            displayHero={true}
            fetchAll={activeUIElement === heroUiElements}
          />
        )}
        {isHeroRendered && navigationContent && navigationContent.length > 1 && (
          <ProviderUniverseMenu
            navigationContent={navigationContent}
            onFocus={onProviderMenuUIElementFocus}
            setIsRendered={setIsProviderMenuRendered}
          />
        )}
        <div className={styles['universe-stripe-container']}>
          {!rootStore.uiStoreProvider.isStripesVisible && isHeroRendered && navigationContent && navigationContent.length > 1 && (
            <StripeSkeleton isPortrait={isFilmUniverseSelected} />
          )}
          {rootStore.uiStoreProvider.isStripesVisible &&
            (isProviderMenuRendered || (navigationContent && navigationContent.length <= 1)) &&
            uiElements.map((uiElement, index) => {
              const indexIncremented = index + 1; // avoid index overlaps with the hero stripe
              return (
                uiElement.hasItems && (
                  <StripesUIElement
                    universeTitle={universeTitle}
                    uIElementIndex={indexIncremented}
                    key={uiElement.id}
                    uiElement={uiElement}
                    onFocus={onProviderUIElementFocus}
                    handleShowAllEnterPress={() => handleonShowAllEnterPress(uiElement)}
                    contentSource={uiElement.contentSource}
                    providerContentProps={uiElement.providerContentProps}
                    style={uiElement.style}
                    focusFirstElement={false}
                    displayHero={false}
                    fetchAll={activeUIElement === uiElement}
                  />
                )
              );
            })}
        </div>
      </StripesContainer>
      )}
      {activeUIElement && openCollections && <Collections uiElement={activeUIElement} />}
      {isOffline && lastNetworkErrorSource.current !== null && (
        <NetworkErrorDialog onTryAgain={retryNetworkAction} setNetworkStatusOnRetry={true} />
      )}
    </React.Fragment>
  );
});

export default StripesProvider;
